'use client';

import React, {
  createContext,
  useContext,
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useCallback,
} from 'react';
import clsx from 'clsx';
import { SymbolType, CdnObjectType, CdnObjectContext } from '@/types';
import { resolvePhoneme } from '@/utils/sounds';
import { convertSentenceToSlug } from '@/utils/sentences';
import { Alert, AlertType } from '@/components';

export type TooltipPosition =
  | 'topLeft'
  | 'top'
  | 'topRight'
  | 'rightTop'
  | 'right'
  | 'rightBottom'
  | 'bottomRight'
  | 'bottom'
  | 'bottomLeft'
  | 'leftBottom'
  | 'left'
  | 'leftTop';

export type TooltipTrigger = 'hover' | 'click';

interface Tooltip {
  show: (
    newContent: string,
    newElementPosition?: DOMRect,
    newTooltipPosition?: TooltipPosition,
  ) => void;
  hide: () => void;
}

export const Tooltip = forwardRef<Tooltip>((props, ref) => {
  const [elementPosition, setElementPosition] = useState<DOMRect | undefined>();

  const [tooltipPosition, setTooltipPosition] =
    useState<TooltipPosition>('top');

  const [content, setContent] = useState<string | undefined>();

  useImperativeHandle(
    ref,
    () => ({
      show: (newContent, newElementPosition, newTooltipPosition) => {
        setElementPosition(newElementPosition);
        setTooltipPosition(newTooltipPosition || 'top');
        setContent(newContent);
      },
      hide: () => {
        setElementPosition(undefined);
        setContent(undefined);
      },
    }),
    [],
  );

  if (!elementPosition || !content) {
    return null;
  }

  const arrowStyles = {
    topLeft:
      'border-x-8 border-x-transparent border-t-8 border-t-pt-secondary-accent border-opacity-90 top-full left-3 -translate-y-2',
    top: 'border-x-8 border-x-transparent border-t-8 border-t-secondary-accent border-opacity-90 top-full left-1/2 -translate-y-2 -translate-x-1/2',
    topRight:
      'border-x-8 border-x-transparent border-t-8 border-t-secondary-accent border-opacity-90 top-full right-3 -translate-y-2',
    rightTop:
      'border-y-8 border-y-transparent border-r-8 border-r-secondary-accent border-opacity-90 right-full top-3 translate-x-full',
    right:
      'border-y-8 border-y-transparent border-r-8 border-r-secondary-accent border-opacity-90 right-full top-1/2 -translate-y-1/2 translate-x-full',
    rightBottom:
      'border-y-8 border-y-transparent border-r-8 border-r-secondary-accent border-opacity-90 right-full bottom-3 translate-x-full',
    bottomRight:
      'border-x-8 border-x-transparent border-b-8 border-b-secondary-accent border-opacity-90 bottom-full right-3 translate-y-2',
    bottom:
      'border-x-8 border-x-transparent border-b-8 border-b-secondary-accent border-opacity-90 bottom-full left-1/2 translate-y-2 -translate-x-1/2',
    bottomLeft:
      'border-x-8 border-x-transparent border-b-8 border-b-secondary-accent border-opacity-90 bottom-full left-3 translate-y-2',
    leftBottom:
      'border-y-8 border-y-transparent border-l-8 border-l-secondary-accent border-opacity-90 left-full bottom-3 -translate-x-full',
    left: 'border-y-8 border-y-transparent border-l-8 border-l-secondary-accent border-opacity-90 left-full top-1/2 -translate-y-1/2 -translate-x-full',
    leftTop:
      'border-y-8 border-y-transparent border-l-8 border-l-secondary-accent border-opacity-90 left-full top-3 -translate-x-full',
  }[tooltipPosition];

  const HALF_FACTOR = 2;

  const tooltipPositionStyles = {
    topLeft: {
      top: elementPosition?.top + window.scrollY,
      left: elementPosition?.left + window.scrollX,
    },
    top: {
      top: elementPosition?.top + window.scrollY,
      left:
        elementPosition?.left +
        elementPosition.width / HALF_FACTOR +
        window.scrollX,
    },
    topRight: {
      top: elementPosition?.top + window.scrollY,
      right: elementPosition?.right - elementPosition.width + window.scrollX,
    },
    rightTop: {
      top: elementPosition?.top + window.scrollY,
      left: elementPosition?.right + window.scrollX,
    },
    right: {
      top:
        elementPosition?.top +
        elementPosition.height / HALF_FACTOR +
        window.scrollY,
      left: elementPosition?.right + window.scrollX,
    },
    rightBottom: {
      top: elementPosition?.bottom + window.scrollY,
      left: elementPosition?.right + window.scrollX,
    },
    bottomRight: {
      top: elementPosition?.bottom + window.scrollY,
      right: elementPosition?.right - elementPosition.width + window.scrollX,
    },
    bottom: {
      top: elementPosition?.bottom + window.scrollY,
      left:
        elementPosition?.left +
        elementPosition.width / HALF_FACTOR +
        window.scrollX,
    },
    bottomLeft: {
      top: elementPosition?.bottom + window.scrollY,
      left: elementPosition?.left + window.scrollX,
    },
    leftBottom: {
      top: elementPosition?.bottom + window.scrollY,
      left: elementPosition?.left + window.scrollX,
    },
    left: {
      top:
        elementPosition?.top +
        elementPosition.height / HALF_FACTOR +
        window.scrollY,
      left: elementPosition?.left + window.scrollX,
    },
    leftTop: {
      top: elementPosition?.top + window.scrollY,
      left: elementPosition?.left + window.scrollX,
    },
  }[tooltipPosition];

  return (
    <div
      style={tooltipPositionStyles}
      className={clsx(
        'z-50 fixed',
        tooltipPosition === 'topLeft' && '-translate-y-full pb-2',
        tooltipPosition === 'top' && '-translate-y-full -translate-x-1/2 pb-2',
        tooltipPosition === 'topRight' && '-translate-y-full pb-2',
        tooltipPosition === 'rightTop' && 'pl-2',
        tooltipPosition === 'right' && '-translate-y-1/2 pl-2',
        tooltipPosition === 'rightBottom' && '-translate-y-full pl-2',
        tooltipPosition === 'bottomRight' && 'pt-2',
        tooltipPosition === 'bottom' && '-translate-x-1/2 pt-2',
        tooltipPosition === 'bottomLeft' && 'pt-2',
        tooltipPosition === 'leftBottom' &&
          '-translate-y-full -translate-x-full pr-2',
        tooltipPosition === 'left' && '-translate-x-full -translate-y-1/2 pr-2',
        tooltipPosition === 'leftTop' && '-translate-x-full pr-2',

        !!content ? 'opacity-100' : 'opacity-0',
      )}
    >
      <div
        className={clsx(
          'relative min-w-36 max-w-44 md:min-w-48 md:max-w-96 p-3 bg-pt-secondary-accent text-pt-secondary-accent-child bg-opacity-9 text-xs rounded-lg',
        )}
      >
        {content}
      </div>
      <div
        className={`absolute w-0 h-0 border-solid border-pt-secondary-accent ${arrowStyles}`}
      ></div>
    </div>
  );
});

Tooltip.displayName = 'Tooltip';

interface AlertData {
  message: string;
  type?: AlertType;
  title?: string;
}

interface AlertCradleProps {
  alert?: AlertData;
}

export const AlertCradle = ({ alert }: AlertCradleProps) => {
  const [showing, setShowing] = useState<boolean>(true);

  useEffect(() => {
    setShowing(true);
  }, [alert]);

  return alert && showing ? (
    <div className="fixed bottom-0 left-0 p-6 z-50">
      <Alert
        message={alert.message}
        type={alert.type}
        title={alert.title}
        onClose={() => setShowing(false)}
      />
    </div>
  ) : null;
};

type BgColor = 'sky' | 'grass';

interface UiContextData {
  tooltipRef: React.RefObject<Tooltip | null> | null;
  sendAlert: (
    message: string,
    options?: {
      type?: AlertType;
      title?: string;
    },
  ) => void;
  currentAlert?: AlertData;
  bgColor?: BgColor;
  setBgColor: (bgColor?: BgColor) => void;
  getCdnUrl: (
    symbol: string,
    symbolType: SymbolType,
    objectType: CdnObjectType,
    isExemplar?: boolean,
    context?: CdnObjectContext,
  ) => string | undefined;
  getCdnUrls: (
    symbol: string | Array<string>,
    symbolType: SymbolType,
    objectType: CdnObjectType,
    isExemplar?: boolean,
    context?: CdnObjectContext,
  ) => string | Array<string> | undefined;
}

const initialValue: UiContextData = {
  tooltipRef: null,
  sendAlert: () => undefined,
  currentAlert: undefined,
  bgColor: undefined,
  setBgColor: () => undefined,
  getCdnUrl: () => undefined,
  getCdnUrls: () => undefined,
};

const UiContext = createContext<UiContextData>(initialValue);

const ALERT_TIMEOUT = 10000;

const parseSymbol = (symbol: string, symbolType: SymbolType) =>
  symbolType === SymbolType.Sentence
    ? convertSentenceToSlug(symbol).toLowerCase()
    : symbol.replaceAll('.', '').toLowerCase();

const getCdnUrl = (
  symbol: string,
  symbolType: SymbolType,
  objectType: CdnObjectType,
  isExemplar: boolean = false,
  context?: CdnObjectContext,
) => {
  const baseUrl =
    'https://pickatale-dev-studio-cdn.s3.eu-west-1.amazonaws.com/phonics/assets';

  const getObjectTypeForUrl = () => {
    switch (objectType) {
      case CdnObjectType.Image:
        return 'images';
      case CdnObjectType.Audio:
        return 'audio';
    }
  };

  const getSymbolTypeForUrl = () => {
    switch (symbolType) {
      case SymbolType.Sound:
        return 'sounds';
      case SymbolType.Word:
        return 'words';
      case SymbolType.Sentence:
        return 'sentences';
    }
  };

  const getFileNameForUrl = () =>
    symbolType === SymbolType.Sound && isExemplar
      ? `${symbol.replace('?', '').toLowerCase()}-exemplar`
      : symbolType === SymbolType.Sound
        ? resolvePhoneme(symbol, context?.word)
        : symbol.replace('?', '').toLowerCase();

  const getFileExtension = () => {
    switch (objectType) {
      case CdnObjectType.Image:
        return 'png';
      case CdnObjectType.Audio:
        return 'mp3';
    }
  };

  return `${[
    baseUrl,
    getObjectTypeForUrl(),
    getSymbolTypeForUrl(),
    getFileNameForUrl(),
  ].join('/')}.${getFileExtension()}`;
};

const getCdnUrls = (
  symbol: string | Array<string>,
  symbolType: SymbolType,
  objectType: CdnObjectType,
  isExemplar: boolean = false,
  context?: CdnObjectContext,
) =>
  Array.isArray(symbol)
    ? symbol
        .map((s) =>
          !!s && s !== ''
            ? getCdnUrl(
                parseSymbol(s, symbolType),
                symbolType,
                objectType,
                isExemplar,
                context,
              )
            : undefined,
        )
        .filter((asset): asset is string => !!asset)
    : !!symbol && symbol !== ''
      ? getCdnUrl(
          parseSymbol(symbol, symbolType),
          symbolType,
          objectType,
          isExemplar,
          context,
        )
      : undefined;

export const assetCdnResourceExists = async (
  symbol: string,
  symbolType: SymbolType,
  objectType: CdnObjectType,
  isExemplar: boolean = false,
  context?: CdnObjectContext,
): Promise<boolean> => {
  const fileUrl = getCdnUrl(
    parseSymbol(symbol, symbolType),
    symbolType,
    objectType,
    isExemplar,
    context,
  );

  const response = await fetch(fileUrl, { method: 'GET' });

  return response.ok;
};

export const UiContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const tooltipRef = useRef<Tooltip | null>(null);

  const [currentAlert, setCurrentAlert] = useState<AlertData | undefined>(
    undefined,
  );

  const sendAlert = useCallback(
    (
      message: string,
      options?: {
        type?: AlertType;
        title?: string;
      },
    ) => {
      setCurrentAlert({
        message,
        type: options?.type,
        title: options?.title,
      });
    },
    [],
  );

  const [bgColor, setBgColor] = useState<BgColor | undefined>();

  useEffect(() => {
    if (!currentAlert) {
      return;
    }

    const timeout = setTimeout(() => {
      setCurrentAlert(undefined);
    }, ALERT_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [currentAlert]);

  return (
    <UiContext.Provider
      value={{
        tooltipRef,
        sendAlert,
        currentAlert,
        bgColor,
        setBgColor,
        getCdnUrl,
        getCdnUrls,
      }}
    >
      {children}
    </UiContext.Provider>
  );
};

export const useUi = () => useContext(UiContext);

export const useAssetCdn = (
  symbol: string | Array<string>,
  symbolType: SymbolType,
  objectType: CdnObjectType,
  isExemplar: boolean = false,
  context?: CdnObjectContext,
): string | Array<string> | undefined => {
  const { getCdnUrls } = useUi();

  return getCdnUrls(symbol, symbolType, objectType, isExemplar, context);
};

export const useTooltip = (
  content?: string,
  options?: {
    position?: TooltipPosition;
    trigger?: TooltipTrigger;
  },
) => {
  const { tooltipRef } = useUi();

  const tooltipElementRef = useRef<HTMLElement | null>(null);

  const [tooltipShowing, setTooltipShowing] = useState<boolean>(false);

  const showTooltip = useCallback(() => {
    setTooltipShowing(true);
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltipShowing(false);
  }, []);

  const toggleTooltip = useCallback(() => {
    setTooltipShowing(!tooltipShowing);
  }, [tooltipShowing]);

  const { position = 'top', trigger = 'hover' } = options || {};

  useEffect(() => {
    if (!content) {
      return;
    }
    if (!tooltipRef?.current) {
      return;
    }
    if (!tooltipElementRef?.current) {
      return;
    }

    const tooltipElement = tooltipElementRef.current;

    document.addEventListener('hideTooltip', hideTooltip);

    if (trigger === 'hover') {
      tooltipElement.addEventListener('mouseenter', showTooltip);
      tooltipElement.addEventListener('mouseleave', hideTooltip);
    }

    if (trigger === 'click') {
      tooltipElement.addEventListener('click', toggleTooltip);
    }

    return () => {
      document.removeEventListener('hideTooltip', hideTooltip);

      if (trigger === 'hover') {
        tooltipElement.removeEventListener('mouseenter', showTooltip);
        tooltipElement.removeEventListener('mouseleave', hideTooltip);
      }

      if (trigger === 'click') {
        tooltipElement.removeEventListener('click', toggleTooltip);
      }
    };
  }, [
    hideTooltip,
    showTooltip,
    toggleTooltip,
    content,
    tooltipRef,
    tooltipElementRef,
    trigger,
  ]);

  useEffect(() => {
    if (!content) {
      return;
    }
    if (!tooltipRef?.current) {
      return;
    }
    if (!tooltipElementRef?.current) {
      return;
    }

    const tooltip = tooltipRef.current;
    const tooltipElement = tooltipElementRef.current;

    if (tooltipShowing) {
      tooltip.show(content, tooltipElement.getBoundingClientRect(), position);
    } else {
      tooltip.hide();
    }
  }, [tooltipShowing, content, position, tooltipRef, tooltipElementRef]);

  return {
    tooltipRef,
    tooltipElementRef,
    tooltipShowing,
    showTooltip,
    hideTooltip,
    toggleTooltip,
  };
};
